import { useLazyQuery } from "@apollo/client/react";
import {
  Box,
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  TableCell as MuiTableCell,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { RenderHTML, UploadFile } from "components";
import { useFocusStyles } from "hooks/focusBorder";
import { openUrlUtil } from "hooks/open-url";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UPDATE_COURSE_IN_PROGRESS_FLAG, useAppDispatch } from "store";
import { KLP_COURSE_WINDOW_NAME } from "utils/constants";
import { Activities_activities } from "utils/graphql/Activities";
import * as ActivityUrlTypes from "utils/graphql/ActivityURL";
import * as AssignmentTemplateTypes from "utils/graphql/AssignmentTemplate";
import { ActivityType } from "utils/graphql/Global";
import portalSettingsManager from "utils/portalSettingsManager";
import { GET_ACTIVITY_URL, GET_ASSIGNMENT_TEMPLATE } from "utils/queries";
import { getTimeFromDateTime, getDateFromDateTime, isValidURL } from "utils/helpers";
import { LaunchConfirmDialog } from "./launch-confirm-dialog";

const Toggle = styled(IconButton)<IconButtonProps>(({ theme, className }) => ({
  // Logic for the 'expanded' state
  ...(className === "expanded"
    ? {
        borderRadius: "50%",
        "& svg": {
          transform: "rotate(180deg)",
        },
      }
    : {
        // Logic for the default state
        color: theme.palette.grey[800],
        "& svg": {
          transform: "none",
        },
      }),
}));
Toggle.displayName = "Toggle";

const Launch = styled(Button)<ButtonProps>(({ theme, className }) => ({
  ...(className?.includes("expanded")
    ? {
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        "&:focus": {
          border: "2px solid #000",
        },
      }
    : {
        "&:focus": {
          border: "2px solid #000",
        },
      }),
}));
Launch.displayName = "Launch";

interface SessionDataProps {
  isMobile?: boolean;
}

const SessionData = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<SessionDataProps>(({ theme, isMobile }) => ({
  "& > div": {
    flex: isMobile ? "1 0 49%" : "0 0 16.66%",
    marginTop: "15px",
    fontSize: isMobile ? "12px" : undefined,
    fontWeight: isMobile ? "bold" : undefined,
    "& > span": {
      fontSize: "12px",
      fontWeight: 400,
      color: theme.palette.grey["500"],
      marginBottom: isMobile ? "5px" : undefined,
      display: isMobile ? "inline-block" : undefined,
    },
  },
}));
SessionData.displayName = "SessionData";

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.grey["800"],
}));
TableCell.displayName = "TableCell";

const DescriptionTag = styled("div")({
  wordBreak: "break-word",
  "& *": {
    maxWidth: "100% !important",
  },
});

interface ActivityProps {
  activity: Activities_activities;
  courseID: string;
  source: string;
  showHeader: boolean;
  isFirstActivity: boolean | null;
  showAction: boolean;
  showSession: boolean;
  handleEnroll: () => void;
  handleActivityRefresh: () => void;
  handleOpenWorkshop: (activityId: string) => void;
  activityHeadingLevel?: HeadingLevel;
  dividerHeadingLevel?: HeadingLevel;
}

type HeadingLevel = "h3" | "h4" | "h5";

const getBaseHeadingLevel = (
  activityType: ActivityType,
  activityHeadingLevel?: HeadingLevel,
  dividerHeadingLevel?: HeadingLevel,
): HeadingLevel => {
  if (activityType === ActivityType.DIVIDER) {
    return dividerHeadingLevel || activityHeadingLevel || "h3";
  }

  return activityHeadingLevel || "h3";
};

export const Activity = ({
  activity,
  courseID,
  showHeader,
  isFirstActivity,
  source,
  showAction,
  showSession,
  handleEnroll,
  handleActivityRefresh,
  activityHeadingLevel,
  dividerHeadingLevel,
}: ActivityProps): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const focusClass = useFocusStyles();
  // state used for controlling the dialog window showing more info about available sessions in mobile

  const [expanded, setExpanded] = useState(true);
  const [launchDialog, setLaunchDialog] = useState(false);
  const [updateActivityStatus] = useLazyQuery<ActivityUrlTypes.ActivityURL>(GET_ACTIVITY_URL);

  const [downloadTemplate, templateResponse] =
    useLazyQuery<AssignmentTemplateTypes.AssignmentTemplate>(GET_ASSIGNMENT_TEMPLATE, {
      fetchPolicy: "no-cache",
    });

  const baseHeadingLevel = getBaseHeadingLevel(
    activity?.type,
    activityHeadingLevel,
    dividerHeadingLevel,
  );
  const nextHeadingLevel =
    baseHeadingLevel === "h3" ? "h4" : baseHeadingLevel === "h4" ? "h5" : "h6";

  // Ref to store window opened synchronously on click to avoid popup blocker
  const templateWindowRef = useRef<Window | null>(null);

  function toggle() {
    setExpanded(!expanded);
  }

  const { openUrl } = openUrlUtil();
  const launchCourseWindow = (courseUrl: string) => {
    const { isSuccess, windowEvent } = openUrl(courseUrl, KLP_COURSE_WINDOW_NAME);
    updateActivityStatus({
      variables: {
        activityId: String(activity.id),
        activityType: activity.type,
        courseId: courseID,
      },
    });
    dispatch({ type: UPDATE_COURSE_IN_PROGRESS_FLAG, payload: true });
    const closeWindow = setInterval(function () {
      if (windowEvent?.closed) {
        dispatch({ type: UPDATE_COURSE_IN_PROGRESS_FLAG, payload: false });
        clearInterval(closeWindow);
        if (showSession) {
          handleEnroll();
        } else {
          handleActivityRefresh();
        }
      }
    }, 500);

    if (!isFirstActivity && expanded) {
      setExpanded(false);
    }
  };

  const handleActivityButton = () => {
    if (source === "OPEN_SESAME" || source === "LINKED_IN") {
      setLaunchDialog(true);
      return false;
    }
    launchCourseWindow(activity.url);
  };

  useEffect(() => {
    if (templateResponse?.data && templateResponse?.data?.assignmentTemplateUri) {
      // Navigate the pre-opened window to the template URL
      if (templateWindowRef.current && !templateWindowRef.current.closed) {
        templateWindowRef.current.location.href = templateResponse.data.assignmentTemplateUri;
      }
      templateWindowRef.current = null;
      handleActivityRefresh();
    }
  }, [templateResponse]);

  const launchActivityURL = (activity: any) => {
    launchCourseWindow(activity.url);
    if (!isFirstActivity && expanded) {
      setExpanded(false);
    }
  };

  const transformedDescription =
    activity.description && activity.description !== null && activity.description !== ""
      ? activity.description
          .replace(
            "<strong>",
            `<${nextHeadingLevel} style="font-size: 14px; color: rgb(51, 51, 51); font-weight: 700;">`,
          )
          .replace("</strong>", `</${nextHeadingLevel}>`)
          .replace(
            '<strong style="font-size: 14px;">',
            `<${nextHeadingLevel} style="font-size: 14px; color: rgb(51, 51, 51); font-weight: 700;">`,
          )
      : "";

  return (
    <>
      {showHeader && (
        <Box role="rowgroup">
          <Box
            display="flex"
            p={isMobile ? "0 10px 7px 40px" : "0 10px 7px 60px"}
            borderBottom={`1px solid ${theme.palette.grey["300"]}`}
            style={{ fontSize: "11px", color: theme.palette.grey["800"] }}
            role="row"
          >
            <Box role="columnheader" width="25%" p={{ xs: "10px", sm: "0px" }}>
              <span>{t("name").toTitleCase()}</span>
            </Box>
            <Box role="columnheader" width="25%" p={{ xs: "10px", sm: "0px" }}>
              <span>{t("status")}</span>
            </Box>
            <Box role="columnheader" width="25%" p={{ xs: "10px", sm: "0px" }}>
              <span>{t("type")}</span>
            </Box>
            {showAction && (
              <Box role="columnheader" textAlign="left" width="25%" p={{ xs: "10px", sm: "0px" }}>
                <span>{t("action")}</span>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {activity?.type !== ActivityType.DIVIDER && (
        <Box
          paddingLeft={isMobile ? "0px" : "44px"}
          borderBottom={`1px solid ${theme.palette.grey["300"]}`}
          className={`${expanded ? "expanded" : ""}`}
          role="rowgroup"
        >
          <Box
            role="row"
            display="flex"
            paddingBottom={isMobile ? "10px" : "0px"}
            paddingLeft={isMobile ? "38px" : "0px"}
            alignItems="center"
            minHeight="30px"
            m="10px 0"
            style={{
              fontSize: `${!isMobile ? "12px" : "10px"}`,
              color:
                portalSettingsManager.application?.common?.themeColor ||
                `${theme.palette.grey["800"]}`,
            }}
          >
            <Box role="cell" position="relative" style={{ fontWeight: 700 }} width="25%">
              <Toggle
                aria-label={t("aria_label_click_to_toggle") + " " + activity.name}
                aria-expanded={expanded}
                size="small"
                onClick={toggle}
                style={{
                  position: "absolute",
                  top: "-2px",
                  left: `${!isMobile ? "-32px" : "-32px"}`,
                  backgroundColor: expanded
                    ? portalSettingsManager.buttonColors.themed.active.back
                    : portalSettingsManager.buttonColors?.normal?.active?.back,
                  border: expanded
                    ? portalSettingsManager.buttonColors.themed.active.border
                    : portalSettingsManager.buttonColors?.normal?.active?.border,
                  height: "24px",
                  width: "24px",
                  margin: "auto",
                }}
                className={`${expanded ? "expanded" : ""} ${focusClass.focusItem}`}
              >
                {expanded ? (
                  <KeyboardArrowDown
                    style={{
                      color:
                        portalSettingsManager.buttonColors.themed.active.text ||
                        theme.palette.grey["800"],
                    }}
                  />
                ) : (
                  <KeyboardArrowUp
                    style={{
                      color:
                        portalSettingsManager.buttonColors?.normal?.active?.text ||
                        theme.palette.grey["800"],
                    }}
                  />
                )}
              </Toggle>
              <Typography fontSize={12} fontWeight={600} component={baseHeadingLevel}>
                {activity.name}
              </Typography>
            </Box>
            <Box role="cell" justifyContent="start" width="25%">
              <span>{t(activity.statusCode?.toLowerCase())}</span>
            </Box>
            <Box role="cell" justifyContent="start" width="25%">
              <Box display={{ xs: "block", sm: "block" }}>
                <span aria-label={t(activity.type.toLowerCase()) || ""}>
                  {t(activity.type.toLowerCase())}
                </span>
              </Box>
            </Box>
            {showAction && (
              <Box textAlign="left" width="25%" role="cell">
                {activity.type !== ActivityType.ASSIGNMENT &&
                  activity?.statusCode !== "Processing" &&
                  activity?.statusCode !== "PaymentAwaited" &&
                  activity?.hasLaunchUrl && (
                    <Launch
                      onClick={handleActivityButton}
                      style={{
                        fontSize: "10px",
                        fontWeight: 800,
                        color: expanded
                          ? portalSettingsManager.buttonColors.themed.active.text
                          : portalSettingsManager.buttonColors?.normal?.active?.text ||
                            theme.palette.grey["800"],
                        border: `1px solid ${
                          expanded
                            ? portalSettingsManager.buttonColors.themed.active.border
                            : portalSettingsManager.buttonColors?.normal?.active?.border ||
                              theme.palette.grey["A100"]
                        }`,
                        backgroundColor: expanded
                          ? portalSettingsManager.buttonColors.themed.active.back
                          : portalSettingsManager.buttonColors?.normal?.active?.back,
                        borderRadius: 0,
                        padding: "5px 15px",
                      }}
                      className={`${expanded ? "expanded" : ""} ${focusClass.focusItem} ${
                        focusClass.border800
                      } ${focusClass.secondaryHover}`}
                      aria-describedby={
                        activity?.statusCode?.toLowerCase() === "enrolled"
                          ? t("click_to_launch_course")
                          : t("click_to_relaunch_course")
                      }
                      aria-label={
                        activity?.statusCode?.toLowerCase() === "enrolled"
                          ? t("click_to_launch_course")
                          : t("click_to_relaunch_course")
                      }
                      role="link"
                    >
                      {activity?.statusCode?.toLowerCase() === "enrolled"
                        ? t("launch")
                        : t("re_launch")}
                      <span
                        style={{
                          overflow: "hidden",
                          clip: "rect(0,0,0,0)",
                          position: "absolute",
                          width: "1px",
                          height: "1px",
                          border: 0,
                          wordWrap: "normal",
                        }}
                      >
                        Opens a new window
                      </span>
                    </Launch>
                  )}
                {activity.type === ActivityType.ASSIGNMENT && (
                  <div>
                    {((!activity?.hasContent &&
                      activity?.statusCode !== "Processing" &&
                      activity?.statusCode !== "PaymentAwaited") ||
                      (activity?.hasContent &&
                        activity?.statusCode !== "Processing" &&
                        activity?.statusCode !== "PaymentAwaited" &&
                        activity?.statusCode !== "Enrolled")) && (
                      <div>
                        <UploadFile
                          onUploadSuccess={handleActivityRefresh}
                          activityId={activity.id}
                          courseId={courseID}
                        />
                        <br></br>
                        <Box
                          style={{ fontSize: "13px", color: "#333333", lineHeight: "21px" }}
                          fontWeight={700}
                        >
                          <RenderHTML content={`(doc, docx, pdf, txt) `} />
                        </Box>
                        <br></br>
                      </div>
                    )}
                    {activity?.statusCode === "Completed" && (
                      <Launch
                        onClick={handleActivityButton}
                        style={{
                          fontSize: "10px",
                          fontWeight: 800,
                          color:
                            portalSettingsManager.buttonColors.normal.active.text ||
                            theme.palette.grey["800"],
                          border: `1px solid ${
                            portalSettingsManager.buttonColors.normal.active.border ||
                            theme.palette.grey["800"]
                          }`,
                          backgroundColor:
                            portalSettingsManager.buttonColors.normal.active.back || "transparent",
                          borderRadius: 0,
                          padding: "5px 15px",
                        }}
                        className={`${expanded ? "expanded" : ""}`}
                        title={t("download_msg")}
                        aria-label={t("download_stored_file")}
                      >
                        {t("download_stored_file")}
                      </Launch>
                    )}
                  </div>
                )}
              </Box>
            )}
          </Box>
          {expanded && (
            <>
              <Box
                style={{
                  marginTop: isMobile ? "25px" : "0px",
                  paddingTop: "5px",
                  //borderTop: `1px solid ${theme.palette.grey["300"]}`,
                }}
                role="row"
              >
                <div role="cell" aria-colspan={4}>
                  <Typography
                    style={{ fontSize: "13px", color: "#333333", fontWeight: 700 }}
                    component={nextHeadingLevel}
                  >
                    {t("details").toTitleCase()}
                  </Typography>
                  <DescriptionTag
                    style={{
                      fontSize: "13px",
                      color: theme.palette.grey["800"],
                      lineHeight: "21px",
                      margin: "10px 0 30px 0",
                    }}
                  >
                    <RenderHTML content={transformedDescription} />
                  </DescriptionTag>
                </div>
              </Box>
              {showSession && activity.bookedSession && (
                <Box>
                  {activity.hasSessionInfo && (
                    <>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: theme.palette.grey["800"],
                        }}
                        component={nextHeadingLevel}
                      >
                        {t("session_details")}
                      </Typography>
                      <SessionData role="row" isMobile={isMobile} display="flex" flexWrap="wrap">
                        {activity.bookedSession.isVirtualWorkshop ? (
                          <>
                            <div role="cell">
                              <span>{t("session_id")}</span>
                              <div>{activity.bookedSession.externalSessionId}</div>
                            </div>
                            {isMobile && <div />}
                            <div role="cell">
                              <span>{t("start_date_time")}</span>
                              <div>{activity.bookedSession.startDateTimeDisplay}</div>
                            </div>
                            <div role="cell">
                              <span>{t("end_date_time")}</span>
                              <div>{activity.bookedSession.endDateTimeDisplay}</div>
                            </div>
                            <div role="cell">
                              <span>{t("duration")}</span>
                              <div>{activity.bookedSession.duration}</div>
                            </div>
                            <div role="cell">
                              <span>{t("vc_link")}</span>
                              <div
                                style={{
                                  fontSize: "12px",
                                  textTransform: "none",
                                  color: theme.palette.primary.main,
                                }}
                              >
                                <a
                                  target="_blank"
                                  rel="noreferrer"
                                  href={activity.bookedSession.address?.streetAddress1}
                                >
                                  {t("vc_link_text")}
                                </a>
                              </div>
                            </div>
                            {!isMobile && <div />}
                            {activity.bookedSession.address?.postalCode &&
                              isValidURL(activity.bookedSession.address?.postalCode) && (
                                <div role="cell">
                                  <span>{t("vc_material_link")}</span>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      textTransform: "none",
                                      color: theme.palette.primary.main,
                                    }}
                                  >
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={activity.bookedSession.address?.postalCode}
                                    >
                                      {t("vc_material_link_text")}
                                    </a>
                                  </div>
                                </div>
                              )}
                          </>
                        ) : (
                          <>
                            <div role="cell">
                              <span>{t("session_id")}</span>
                              <div>{activity.bookedSession.externalSessionId}</div>
                            </div>
                            {isMobile && <div />}
                            {activity.bookedSession.startDateTimeDisplay && (
                              <div role="cell">
                                <span>{t("f2f_date")}</span>
                                <div>
                                  {getDateFromDateTime(activity.bookedSession.startDateTimeDisplay)}
                                </div>
                              </div>
                            )}
                            {activity.bookedSession.startDateTimeDisplay && (
                              <div role="cell">
                                <span>{t("f2f_time")}</span>
                                <div>
                                  {getTimeFromDateTime(activity.bookedSession.startDateTimeDisplay)}
                                </div>
                              </div>
                            )}
                            {activity.bookedSession.duration && (
                              <div role="cell">
                                <span>{t("duration")}</span>
                                <div>{activity.bookedSession.duration}</div>
                              </div>
                            )}
                            {activity.bookedSession.location && (
                              <div role="cell">
                                <span>{t("location")}</span>
                                <div>{activity.bookedSession.location}</div>
                              </div>
                            )}
                            {!isMobile && <div />}
                            {activity.bookedSession.address?.name && (
                              <div role="cell">
                                <span>{t("venue_name")}</span>
                                <div>{activity.bookedSession.address?.name}</div>
                              </div>
                            )}
                            {activity.bookedSession.address?.streetAddress1 && (
                              <div role="cell">
                                <span>{t("venue_address_1")}</span>
                                <div style={{ wordBreak: "break-all" }}>
                                  {activity.bookedSession.address?.streetAddress1}
                                </div>
                              </div>
                            )}
                            {activity.bookedSession.address?.streetAddress2 && (
                              <div role="cell">
                                <span>{t("venue_address_2")}</span>
                                <div>{activity.bookedSession.address?.streetAddress2}</div>
                              </div>
                            )}
                            {activity.bookedSession.address?.city && (
                              <div role="cell">
                                <span>{t("venue_city")}</span>
                                <div>{activity.bookedSession.address?.city}</div>
                              </div>
                            )}
                            {activity.bookedSession.address?.state && (
                              <div role="cell">
                                <span>{t("venue_state")}</span>
                                <div>{activity.bookedSession.address?.state}</div>
                              </div>
                            )}
                            {activity.bookedSession.address?.country && (
                              <div role="cell">
                                <span>{t("venue_country")}</span>
                                <div>{activity.bookedSession.address?.country}</div>
                              </div>
                            )}
                            {activity.bookedSession.address?.postalCode && (
                              <div role="cell">
                                <span>{t("venue_postal_code")}</span>
                                <div style={{ wordBreak: "break-all" }}>
                                  {activity.bookedSession.address?.postalCode}
                                </div>
                              </div>
                            )}
                            <div />
                          </>
                        )}
                      </SessionData>
                    </>
                  )}
                </Box>
              )}
              {activity?.type === ActivityType.ASSIGNMENT &&
                activity?.hasContent &&
                activity?.statusCode !== "Processing" &&
                activity?.statusCode !== "PaymentAwaited" &&
                showAction && (
                  <Box role="row">
                    <Box role="cell" aria-colspan={4}>
                      <button
                        type="button"
                        style={{
                          color: theme.palette.primary.main,
                          cursor: "pointer",
                          margin: "15px 0px",
                          fontSize: "14px",
                          background: "none",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                        }}
                        onClick={() => {
                          // Open window synchronously on user click - this won't be blocked
                          const newWindow = window.open("about:blank", "_blank");
                          if (newWindow) {
                            templateWindowRef.current = newWindow;
                          }
                          downloadTemplate({
                            variables: {
                              activityId: String(activity.id),
                              programmeId: courseID,
                            },
                          });
                        }}
                      >
                        {t("download_template")}
                      </button>
                    </Box>
                  </Box>
                )}
            </>
          )}
        </Box>
      )}
      {activity?.type === ActivityType.DIVIDER && (
        <Box borderBottom={`1px solid ${theme.palette.grey["300"]}`} role="rowgroup">
          <Box role="row">
            <Box role="cell" aria-colspan={4}>
              <Typography
                flex="1"
                position="relative"
                style={{ fontWeight: 700, fontSize: "14px", margin: "10px" }}
                component={baseHeadingLevel}
              >
                {activity.name}
              </Typography>
            </Box>
          </Box>
          <Box role="row">
            <Box
              m="10px 0 10px 10px"
              style={{ fontSize: "13px", color: theme.palette.grey["500"], lineHeight: "21px" }}
              role="cell"
              aria-colspan={4}
            >
              <DescriptionTag>
                <RenderHTML content={transformedDescription} />
              </DescriptionTag>
            </Box>
          </Box>
        </Box>
      )}
      {launchDialog && (
        <LaunchConfirmDialog
          open={launchDialog}
          type={source}
          courseId={courseID}
          handleActivityRefresh={handleActivityRefresh}
          loadActivityURL={() => launchActivityURL(activity)}
          activityId={String(activity.id)}
          handleClose={() => setLaunchDialog(false)}
        />
      )}
    </>
  );
};
